import React, { useState, useMemo } from "react";
import { App, Avatar, Badge, Button, List, Popconfirm, Space, Tag, Typography } from "antd";
import { useLanguage } from "src/providers/LanguageProvider";
import { currency, statusMeta } from "./subscriptionDrawerUtil";
import { GroupedLicenseRow, LicenseRow } from "src/reducers/subscription";
import api from "src/services/api";

const { Text } = Typography;

type Props = {
  group: GroupedLicenseRow;
  onManage: (group: GroupedLicenseRow) => void;
  onCancelSingle: (child: LicenseRow) => void;
};

const SubscriptionDrawerChildrenGroupsList: React.FC<Props> = ({ group, onManage, onCancelSingle }) => {
  const { t } = useLanguage();
  const { message } = App.useApp();

  const countIsOne = group?.count === 1;
  const singleChild = countIsOne ? group.items[0] : null;

  const canShowSingleCancel = Boolean(singleChild && singleChild.isMonthly && !singleChild.isRequired);
  const [loadingCancel, setLoadingCancel] = useState(false);

  const hasChildren = countIsOne && !!singleChild?.children?.length;

  const isMonthly: boolean = useMemo(() => {
    if (typeof (group as any)?.isMonthly === "boolean") return (group as any).isMonthly;
    if (singleChild && typeof singleChild.isMonthly === "boolean") return singleChild.isMonthly;
    return false;
  }, [group, singleChild]);

  const handleCancelLicense = async (child: LicenseRow) => {
    setLoadingCancel(true);
    try {
      await api.get("/licenses/cancel/" + child.Id);
      message.success(t("SUBSCRIPTION_CANCELLED"));
      onCancelSingle?.(child);
    } catch (e) {
      console.error(e);
      message.error(t("SUBSCRIPTION_CANCEL_FAILED"));
    } finally {
      setLoadingCancel(false);
    }
  };

  return (
    <List.Item
      actions={[
        countIsOne && !hasChildren ? (
          canShowSingleCancel ? (
            <Popconfirm
              key="cancel-single"
              title={t("CONFIRM_CANCEL_CHILD_TITLE")}
              description={t("CONFIRM_CANCEL_CHILD_DESC")}
              okText={t("YES")}
              cancelText={t("NO")}
              onConfirm={() => singleChild && handleCancelLicense(singleChild)}
              disabled={singleChild?.status === "canceled"}
            >
              <Button loading={loadingCancel} size="small" danger disabled={singleChild?.status === "canceled"}>
                {t("BTN_CANCEL")}
              </Button>
            </Popconfirm>
          ) : null
        ) : (
          <Button key="manage" size="small" onClick={() => onManage(group)}>
            {t("BTN_DETAILS")}
          </Button>
        ),
        <Tag key="status" color={statusMeta[group.status]?.color}>
          {statusMeta[group.status]?.text}
        </Tag>,
      ]}
    >
      <List.Item.Meta
        avatar={
          group.count > 1 ? (
            <Badge count={`×${group.count}`} offset={[0, 6]}>
              <Avatar shape="square" src={group.img} />
            </Badge>
          ) : (
            <Avatar shape="square" src={group.img} />
          )
        }
        title={
          <Space size={6} wrap>
            <Text strong>{group.service}</Text>
            <Text type="secondary">•</Text>
            <Text type="secondary">{isMonthly ? t("MONTHLY") : t("ONE_TIME")}</Text>
          </Space>
        }
        description={
          <Space direction="vertical" size={0}>
            <Text type="secondary">
              {currency(group.amount)} {" / "} {currency(group.totalAmount)}
              {isMonthly ? ` ${t("PER_MONTH_SHORT")}` : ""}
            </Text>

            <Text>
              <Text strong>{currency(group.totalAmount)}</Text> × {group.count} ={" "}
              <Text strong>{currency(group.total)}</Text>{" "}
              {isMonthly ? t("PER_MONTH_SHORT") : t("ONE_TIME_SHORT")}
            </Text>
          </Space>
        }
      />
    </List.Item>
  );
};

export default SubscriptionDrawerChildrenGroupsList;
